import React from 'react';

import { Skeleton } from '@mui/material';
import { t } from 'i18next';

import BdsCardContainer from '../../../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import ErrorCard from '../../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import MetricTile from '../../../../../../shared/components/v3/MetricTile/MetricTile';
import { IChallengesSummaryValues, IMetricsChallengesSummary } from '../../../../../../shared/interfaces/metrics';
import { TRANSLATIONS_METRICS_NE } from '../../../../../../shared/translations/metrics';
import { formatPercentage, toLocaleNumberFormat } from '../../../../../../shared/utils/utils';

import './ChallengesSummaryCard.scss';

interface IChallengesSummaryCard {
  data?: IMetricsChallengesSummary;
  loading: boolean;
  error: boolean;
  onRetry?: () => void;
}

interface ISummaryMetric {
  key: keyof IChallengesSummaryValues;
  label: string;
  isRate?: boolean;
}

const SUMMARY_METRICS: ISummaryMetric[] = [
  { key: 'activeChallenges', label: TRANSLATIONS_METRICS_NE.challenges.summary.activeChallenge },
  { key: 'totalParticipants', label: TRANSLATIONS_METRICS_NE.challenges.summary.totalParticipants },
  { key: 'participationRate', label: TRANSLATIONS_METRICS_NE.challenges.summary.participationRate, isRate: true },
  { key: 'completionRate', label: TRANSLATIONS_METRICS_NE.challenges.summary.completionRate, isRate: true },
  { key: 'betterfliesGiven', label: TRANSLATIONS_METRICS_NE.challenges.summary.betterfliesDelivered }
];

const formatValue = (value: number | null | undefined, isRate?: boolean): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return isRate ? formatPercentage(value * 100) : toLocaleNumberFormat(value);
};

const ChallengesSummaryCard = ({ data, loading, error, onRetry }: IChallengesSummaryCard) => {
  const renderContent = () => {
    return (
      <div className="challenges-summary-grid">
        {SUMMARY_METRICS.map((metric) => {
          const current = data?.currentMonth?.[metric.key];
          const previous = data?.previousMonth?.[metric.key];
          const showTrend = current !== null && current !== undefined && previous !== null && previous !== undefined;

          let trend: 'up' | 'down' | undefined;
          if (showTrend) {
            trend = current >= previous ? 'up' : 'down';
          }

          return (
            <MetricTile
              key={metric.key}
              label={t(metric.label)}
              value={formatValue(current, metric.isRate)}
              trend={trend}
              tooltip={TRANSLATIONS_METRICS_NE.challenges.summary.tooltip}
            />
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <Skeleton className="rounded-4xl" variant="rectangular" width="100%" height={229} />;
  }

  if (error) {
    return (
      <BdsCardContainer className="challenges-summary-card" borderRadiusSize="radius-l" paddingSize="padding-l">
        <h3 className="challenges-summary-title">{t(TRANSLATIONS_METRICS_NE.challenges.summary.title)}</h3>
        <ErrorCard
          primaryText={t(TRANSLATIONS_METRICS_NE.challenges.error.title)}
          secondaryText={t(TRANSLATIONS_METRICS_NE.challenges.error.description)}
          buttonText={t(TRANSLATIONS_METRICS_NE.challenges.retry)}
          action={onRetry}
        />
      </BdsCardContainer>
    );
  }

  return (
    <BdsCardContainer className="challenges-summary-card" borderRadiusSize="radius-l" paddingSize="padding-l">
      <h3 className="challenges-summary-title">{t(TRANSLATIONS_METRICS_NE.challenges.summary.title)}</h3>
      {renderContent()}
    </BdsCardContainer>
  );
};

export default ChallengesSummaryCard;
